/**
 * @file
 * RAOC-22: pure derivation of the accessible product/noun list (plus the
 * third-party configs) from the resolved combined-config responses.
 *
 * Extracted as a pure function (no React, no store) so it can be:
 * - invoked SYNCHRONOUSLY the moment the config resolves — publishing the
 *   products in the same commit that sets the config responses, instead of
 *   waiting for a second effect → render → commit round-trip; and
 * - unit-tested in isolation.
 *
 * The `> 0` townsquare noun threshold and the Rovo-enabled third-party branch
 * are intentionally preserved so behaviour is identical to the previous
 * (effect-based) derivation.
 */

import { type TSearchConfigAPIResponse } from '@atlassian/search-client/fetch-search-config';

import { type NounKeys } from '../../../common/constants/types';
import {
	type ForgeConnectorProductKey,
	type ThirdPartyConfigsBootstrap,
} from '../../../common/types/schemas/types';
import { type ProductKeys3P } from '../../../common/ui/configs/types';
import {
	EXTRACT_PRODUCT_FROM_ARI,
	isRovoEnabledForSite,
} from '../../../utils/site-metadata-helpers';

/**
 * The mapped third-party response shape consumed by the derivation: the product
 * keys and their configs, as produced by `map3pResourcesToProductKeys`.
 */
export type CombinedThirdPartyResponse = {
	productKeys?: (ProductKeys3P | ForgeConnectorProductKey)[];
	thirdPartyConfigs?: ThirdPartyConfigsBootstrap;
};

/**
 * The derived accessible-products state published by `useRovoConfig`.
 */
export type AccessibleProductsConfig = {
	products?: string[];
	nouns: NounKeys[];
	thirdPartyConfigs?: ThirdPartyConfigsBootstrap;
};

/**
 * Derive the accessible product/noun list from the resolved config responses.
 *
 * - First-party products are the distinct product segments extracted from each
 *   first-party resource ARI.
 * - Nouns are the `project` / `goal` resources; if ANY noun is present
 *   (`> 0`) Townsquare is added to the accessible products.
 * - Third-party product keys are only included when Rovo is enabled for the
 *   site; otherwise the returned `thirdPartyConfigs` is empty.
 *
 * @param firstPartyResponse the adapted first-party config response.
 * @param combinedThirdPartyResponse the mapped third-party product keys/configs.
 * @param cloudIdProp the current cloudId (used for the Rovo-enabled check).
 */
export const deriveAccessibleProductsConfig = (
	firstPartyResponse: TSearchConfigAPIResponse,
	combinedThirdPartyResponse: CombinedThirdPartyResponse,
	cloudIdProp: string | undefined,
): AccessibleProductsConfig => {
	const allAccessibleProducts: string[] = [];

	const firstPartyProducts = [
		...new Set(
			firstPartyResponse.resources.flatMap((resource) => {
				const { id } = resource;
				const match = id.match(EXTRACT_PRODUCT_FROM_ARI);

				if (match && match.length >= 2 && match[1]) {
					return [match[1]];
				}

				return [];
			}),
		),
	];

	allAccessibleProducts.push(...firstPartyProducts);

	const accessibleNouns: NounKeys[] = firstPartyResponse.resources.flatMap((resource) => {
		const { id } = resource;
		const match = id.match(EXTRACT_PRODUCT_FROM_ARI);

		if (match && match.length >= 2 && (match[1] === 'project' || match[1] === 'goal')) {
			return [match[1] as NounKeys];
		}

		return [];
	});

	// If nouns are available we also add Townsquare to the list of accessible products.
	if (accessibleNouns.length > 0) {
		allAccessibleProducts.push('townsquare');
	}

	const doesUserHaveRovo = isRovoEnabledForSite(firstPartyResponse, cloudIdProp);
	if (doesUserHaveRovo) {
		const thirdPartyProductKeys = combinedThirdPartyResponse.productKeys || [];
		allAccessibleProducts.push(...(thirdPartyProductKeys as string[]));
		return {
			products: allAccessibleProducts,
			nouns: accessibleNouns,
			thirdPartyConfigs: combinedThirdPartyResponse.thirdPartyConfigs,
		};
	}

	return {
		products: allAccessibleProducts,
		nouns: accessibleNouns,
		thirdPartyConfigs: {},
	};
};
